/**
 * Variant-driven sections
 * ------------------------------------------------------------------
 * Drives sections whose content changes with the selected variant:
 *
 *   data-variant-section="gallery"   Product Details Gallery
 *   data-variant-section="showcase"  Product Showcase Grid
 *   data-variant-section="size"      Rover Size Comparison
 *
 * Liquid renders the correct state for the variant that is selected on
 * page load, and also emits a JSON payload holding the data for every
 * variant that has its own. This script only handles what happens after
 * the shopper changes variant, so there is never a flash of wrong content.
 *
 * Payload shape:
 *   { "default": <payload|null>, "variants": { "<variantId>": <payload|null> } }
 *
 * A variant id is only listed when that variant carries its own data.
 * Anything else falls back to "default" (the product metafield or the
 * section's blocks, already resolved in Liquid). A null payload means
 * there is nothing to show, and the section hides itself completely.
 */
(function () {
  'use strict';

  var FADE_MS = 180;
  var instances = [];

  /* ----------------------------------------------------------------
     Helpers
     ---------------------------------------------------------------- */

  function parsePayloads(root) {
    var el = root.querySelector('[data-variant-section-data]');
    var empty = { default: null, variants: {} };
    if (!el) return empty;
    try {
      var parsed = JSON.parse(el.textContent);
      return {
        default: parsed.default || null,
        variants: parsed.variants || {}
      };
    } catch (e) {
      return empty;
    }
  }

  function isEmptyPayload(payload) {
    if (!payload) return true;
    if (Array.isArray(payload.cards)) return payload.cards.length === 0;
    if (Object.prototype.hasOwnProperty.call(payload, 'image')) {
      return !payload.image && !payload.hasBlocks;
    }
    return false;
  }

  function setText(el, value) {
    if (!el) return;
    if (value) {
      el.textContent = value;
      el.hidden = false;
    } else {
      el.textContent = '';
      el.hidden = true;
    }
  }

  // Width and height travel with each payload so the box is reserved before
  // the new image decodes, which keeps a variant switch from jumping.
  function setImage(img, src, alt, width, height) {
    if (!img) return;
    if (src) {
      if (img.getAttribute('src') !== src) {
        img.removeAttribute('srcset');
        img.setAttribute('src', src);
      }
      img.setAttribute('alt', alt || '');
      if (width && height) {
        img.setAttribute('width', width);
        img.setAttribute('height', height);
      } else {
        img.removeAttribute('width');
        img.removeAttribute('height');
      }
      img.hidden = false;
    } else {
      img.hidden = true;
    }
  }

  /* ----------------------------------------------------------------
     Renderers - one per section type
     ---------------------------------------------------------------- */

  var renderers = {
    gallery: function (root, payload) {
      var track = root.querySelector('[data-variant-section-target]');
      var template = root.querySelector('[data-variant-section-template]');
      if (!track || !template) return;

      var fragment = document.createDocumentFragment();

      payload.cards.forEach(function (card) {
        var node = template.content.firstElementChild.cloneNode(true);
        var wrap = node.querySelector('[data-card-image-wrap]');

        setImage(node.querySelector('[data-card-image]'), card.image, card.alt || card.title, card.w, card.h);
        if (wrap) wrap.hidden = !card.image;

        setText(node.querySelector('[data-card-title]'), card.title);
        setText(node.querySelector('[data-card-desc]'), card.description);

        fragment.appendChild(node);
      });

      track.innerHTML = '';
      track.appendChild(fragment);
      track.scrollLeft = 0;
    },

    showcase: function (root, payload) {
      setImage(
        root.querySelector('[data-showcase-image]'),
        payload.image,
        payload.alt,
        payload.w,
        payload.h
      );
    },

    size: function (root, payload) {
      var grid = root.querySelector('[data-variant-section-target]');
      var template = root.querySelector('[data-variant-section-template]');
      if (!grid || !template) return;

      var fragment = document.createDocumentFragment();

      payload.cards.forEach(function (card) {
        var node = template.content.firstElementChild.cloneNode(true);

        setImage(node.querySelector('[data-card-product-image]'), card.product_image, card.title, card.pw, card.ph);
        setImage(
          node.querySelector('[data-card-model-image]'),
          card.model_image || card.product_image,
          card.title,
          card.model_image ? card.mw : card.pw,
          card.model_image ? card.mh : card.ph
        );
        setText(node.querySelector('[data-card-title]'), card.title);
        setText(node.querySelector('[data-card-badge]'), card.badge);

        fragment.appendChild(node);
      });

      grid.innerHTML = '';
      grid.appendChild(fragment);
    }
  };

  /* ----------------------------------------------------------------
     Horizontal scrolling: pointer drag + mouse wheel
     ---------------------------------------------------------------- */

  function enableDragScroll(track) {
    var dragging = false;
    var startX = 0;
    var startScroll = 0;
    var travelled = 0;

    track.addEventListener('pointerdown', function (evt) {
      // Touch devices already scroll this natively and much better.
      if (evt.pointerType === 'touch') return;
      if (track.scrollWidth <= track.clientWidth) return;

      dragging = true;
      travelled = 0;
      startX = evt.clientX;
      startScroll = track.scrollLeft;
      track.classList.add('is-dragging');
    });

    window.addEventListener('pointermove', function (evt) {
      if (!dragging) return;
      var delta = evt.clientX - startX;
      travelled = Math.abs(delta);
      if (travelled > 3) evt.preventDefault();
      track.scrollLeft = startScroll - delta;
    }, { passive: false });

    function endDrag() {
      if (!dragging) return;
      dragging = false;
      track.classList.remove('is-dragging');
    }

    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);

    // A drag that ends on a link or button must not also fire a click.
    track.addEventListener('click', function (evt) {
      if (travelled > 5) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    }, true);
  }

  function enableWheelScroll(track) {
    var settleTimer = null;

    track.addEventListener('wheel', function (evt) {
      if (!evt.deltaY) return;
      if (track.scrollWidth <= track.clientWidth) return;

      var atStart = track.scrollLeft <= 0;
      var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;

      // Hand the gesture back to the page once the track has run out, so
      // the shopper is never trapped inside the carousel.
      if ((evt.deltaY < 0 && atStart) || (evt.deltaY > 0 && atEnd)) return;

      evt.preventDefault();
      track.classList.add('is-wheeling');
      track.scrollLeft += evt.deltaY;

      clearTimeout(settleTimer);
      settleTimer = setTimeout(function () {
        track.classList.remove('is-wheeling');
      }, 140);
    }, { passive: false });
  }

  /* ----------------------------------------------------------------
     Section instance
     ---------------------------------------------------------------- */

  function VariantSection(root) {
    this.root = root;
    this.type = root.dataset.variantSection;
    this.payloads = parsePayloads(root);
    this.body = root.querySelector('[data-variant-section-body]') || root;
    this.rendered = root.dataset.variantSectionInitial || 'default';

    // Some sections carry their padding on the Shopify section wrapper, so
    // hiding only the inner container would still leave a gap on the page.
    this.wrapper = root.closest('.shopify-section') ||
      root.closest('[id^="shopify-section-"]');
    this.syncWrapper();

    var track = root.querySelector('[data-variant-section-scroll]');
    if (track) {
      enableDragScroll(track);
      enableWheelScroll(track);
    }

    this.preload();
  }

  VariantSection.prototype.syncWrapper = function () {
    if (!this.wrapper) return;
    this.wrapper.classList.toggle('variant-section-empty', this.root.hidden);
  };

  VariantSection.prototype.payloadFor = function (variantId) {
    var variants = this.payloads.variants;
    if (variantId != null && Object.prototype.hasOwnProperty.call(variants, String(variantId))) {
      return variants[String(variantId)];
    }
    return this.payloads.default;
  };

  // Warm the browser cache so switching variants does not pop in.
  VariantSection.prototype.preload = function () {
    var self = this;
    var run = function () {
      var urls = [];
      Object.keys(self.payloads.variants).forEach(function (key) {
        var payload = self.payloads.variants[key];
        if (!payload) return;
        if (payload.image) urls.push(payload.image);
        (payload.cards || []).forEach(function (card) {
          if (card.image) urls.push(card.image);
          if (card.product_image) urls.push(card.product_image);
          if (card.model_image) urls.push(card.model_image);
        });
      });
      urls.slice(0, 40).forEach(function (url) {
        var img = new Image();
        img.decoding = 'async';
        img.src = url;
      });
    };

    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(run, { timeout: 3000 });
    } else {
      setTimeout(run, 1200);
    }
  };

  VariantSection.prototype.apply = function (variantId) {
    var key = variantId == null ? 'default' : String(variantId);
    var payload = this.payloadFor(variantId);
    var resolvedKey = Object.prototype.hasOwnProperty.call(this.payloads.variants, key)
      ? key
      : 'default';

    // Nothing changed for this variant, leave the DOM alone.
    if (resolvedKey === this.rendered) return;
    this.rendered = resolvedKey;

    if (isEmptyPayload(payload)) {
      this.root.hidden = true;
      this.syncWrapper();
      return;
    }

    this.root.hidden = false;
    this.syncWrapper();

    var renderer = renderers[this.type];
    if (!renderer) return;

    var body = this.body;
    var self = this;

    body.style.transition = 'opacity ' + FADE_MS + 'ms ease';
    body.style.opacity = '0';

    setTimeout(function () {
      renderer(self.root, payload);
      body.style.opacity = '1';
    }, FADE_MS);
  };

  /* ----------------------------------------------------------------
     Boot
     ---------------------------------------------------------------- */

  function init(scope) {
    (scope || document).querySelectorAll('[data-variant-section]').forEach(function (root) {
      if (root.dataset.variantSectionReady === 'true') return;
      root.dataset.variantSectionReady = 'true';
      instances.push(new VariantSection(root));
    });
  }

  document.addEventListener('variant:change', function (evt) {
    var variant = evt.detail && evt.detail.variant;
    var variantId = variant ? variant.id : null;
    instances.forEach(function (instance) {
      instance.apply(variantId);
    });
  });

  // Theme editor re-renders a section in place.
  document.addEventListener('shopify:section:load', function (evt) {
    instances = instances.filter(function (instance) {
      return document.contains(instance.root);
    });
    init(evt.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }
})();
